import { toast } from "sonner";
/* eslint-disable jsx-a11y/anchor-is-valid */
import * as React from "react";
import {useEffect, useState} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {compareBy, Order, stableSort} from "../../helpers/TableHelpers";
import {FilterCombobox} from "../common/FilterCombobox";
import SortableTableColumnHeader from "../common/SortableTableColumnHeader";
import JobRequestStatusChip from "../Jobs/JobRequestStatusChip";
import {JobRequestStatus, JobRequestStatusColors, JobRequestStatusIcons, JobRequestStatusLabels,} from "../../models/Jobs/JobRequestStatus";
import {DeleteIntakeRequest, JobRequest} from "../../models/Jobs/JobRequest";
import AbortJobModal from "../Jobs/AbortJobModal";
import JobsRepository from "../../repositories/JobsRepository";
import Fuse from "fuse.js";
import ConfirmMigrationEntitiesDeletionModal, {CONFIRM_MIGRATION_ENTITIES_DELETION_CONFIGS} from "../common/ConfirmMigrationEntitiesDeletionModal";
import { cn } from "@/lib/utils";
import {
  drawerFiltersBoxClassName,
  drawerFilterItemClassName,
  drawerSearchClassName,
  drawerTableWrapperClassName,
} from "../common/Table/TableStyles";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Search, Trash2, XCircle } from "lucide-react";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { BulkDeletionSummary } from "../common/BulkDeletionSummary";

interface DeleteIntakesTableProps {
  organizationId: string;
  newIntakesForDeletion: DeleteIntakeRequest[];
  setNewIntakesForDeletion: React.Dispatch<React.SetStateAction<DeleteIntakeRequest[]>>;
}

const DeleteIntakesTable: React.FC<DeleteIntakesTableProps> = ({
  organizationId,
  newIntakesForDeletion,
  setNewIntakesForDeletion,
}) => {
  const [order, setOrder] = React.useState<Order>("asc");
  const [sort, setSort] = React.useState<keyof JobRequest>("status");
  const [statusFilter, setStatusFilter] = React.useState<string | null>(null);
  const [searchForIntakeNumber, setSearchForIntakeNumber] = useState<string | null>("");
  const [abortJob, setAbortJob] = useState<JobRequest | null>(null);
  const [confirmDeletionModalOpen, setConfirmDeletionModalOpen] = useState<boolean>(false);
  const [intakesDeletionStatus, setIntakesDeletionStatus] = useState<DeleteIntakeRequest[] | null>(null);
  const jobsRepository = new JobsRepository();
  const queryClient = useQueryClient();

  const intakesQuery = useQuery({ queryKey: [QUERY_KEYS.INTAKES_DELETION_STATUS, organizationId], queryFn:
    async () => {
      return await jobsRepository.getIntakesDeletionStatus(organizationId);
    }, staleTime: 5 * 60 * 1000,
      refetchInterval: 5 * 60 * 1000 + 1,
      refetchIntervalInBackground: true,
      refetchOnMount: true, });

  useEffect(() => {
    if (intakesQuery.data) {
      setIntakesDeletionStatus(intakesQuery.data);
    }
  }, [intakesQuery]);

  const handleSearch = (newIntakeNumber: string) => {
    setSearchForIntakeNumber(newIntakeNumber ?? null);
  };

  const changeSort = (newSort: keyof JobRequest) => {
    setSort(newSort);
  };

  const changeOrder = (newOrder: Order) => {
    setOrder(newOrder);
  };

  const { isError, isFetching } = intakesQuery;

  const renderFilters = () => (
    <React.Fragment>
      <div className={drawerFilterItemClassName}>
        <Label className="text-sm">Status</Label>
        <FilterCombobox
          value={statusFilter}
          onChange={(newValue) => {
            setStatusFilter(newValue);
          }}
          placeholder="Status"
          options={Object.values(JobRequestStatus).map((status) => {
            const deleteRequestStatus = status as JobRequestStatus;
            return {
              value: deleteRequestStatus,
              label: JobRequestStatusLabels[deleteRequestStatus],
              color: JobRequestStatusColors[deleteRequestStatus],
              icon: JobRequestStatusIcons[deleteRequestStatus],
            };
          })}
        />
      </div>
    </React.Fragment>
  );

  const filteredJobs = (intakesDeletionStatus || []).filter((job) => {
    return statusFilter == null || job.status === statusFilter;
  });

  const searchOptions = {
    keys: ["intakeNumber"],
    includeScore: true,
    threshold: 0.1,
    caseSensitive: false,
  };

  const fuse = new Fuse(filteredJobs, searchOptions);
  const searchResults = searchForIntakeNumber
    ? fuse.search(searchForIntakeNumber).map((result) => result.item)
    : filteredJobs;
  const sortedJobs = sort
    ? stableSort<DeleteIntakeRequest>(searchResults, compareBy(order, sort))
    : filteredJobs;
  const allRows = [...newIntakesForDeletion, ...sortedJobs];
  const newIds = new Set(newIntakesForDeletion.map((c) => c.migrationEntityId));

  return (
    <div className="flex flex-col w-full flex-1 min-h-0">
      <div className={drawerFiltersBoxClassName}>
        <div className={drawerSearchClassName}>
          <Label className="text-sm">Search intakes</Label>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              className="pl-8"
              placeholder="Intake number"
              onChange={(event) => handleSearch(event.target.value)}
            />
          </div>
        </div>
        {renderFilters()}
      </div>
      <Button
        disabled={!(newIntakesForDeletion.length > 0)}
        onClick={() => setConfirmDeletionModalOpen(true)}
      >
        Delete Intakes
      </Button>
      <div className={cn(drawerTableWrapperClassName, (isFetching || isError) && "opacity-50 pointer-events-none")}>
        <Table containerClassName="flex-1 min-h-0">
            <TableHeader>
              <TableRow>
                <TableHead className="w-0 text-center" />

                <TableHead>
                  <SortableTableColumnHeader
                    label={"Intake Number"}
                    sortProperty={"intakeNumber"}
                    getSort={() => sort}
                    setSort={(newSort) =>
                      changeSort(newSort as keyof JobRequest)
                    }
                    getOrder={() => order}
                    setOrder={(newOrder) => changeOrder(newOrder)}
                  />
                </TableHead>

                <TableHead>
                  <SortableTableColumnHeader
                    label={"Status"}
                    sortProperty={"status"}
                    getSort={() => sort}
                    setSort={(newSort) =>
                      changeSort(newSort as keyof JobRequest)
                    }
                    getOrder={() => order}
                    setOrder={(newOrder) => changeOrder(newOrder)}
                  />
                </TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>

            <TableBody>
              {allRows.map((deleteIntakeJob) => {
                const canEdit =
                  deleteIntakeJob.status === JobRequestStatus.Pending ||
                  deleteIntakeJob.status === JobRequestStatus.New ||
                  deleteIntakeJob.status === JobRequestStatus.Completed;
                return (
                  <TableRow
                    key={deleteIntakeJob.id ?? deleteIntakeJob.migrationEntityId}
                    className={cn(newIds.has(deleteIntakeJob.migrationEntityId) && "bg-primary/10")}
                  >
                    <TableCell className="text-center" />
                    <TableCell>
                      <span className="text-sm">
                        {deleteIntakeJob.intakeNumber}
                        {deleteIntakeJob.bulk && <BulkDeletionSummary bulk={deleteIntakeJob.bulk} />}
                      </span>
                    </TableCell>
                    <TableCell>
                      <JobRequestStatusChip status={deleteIntakeJob.status} />
                    </TableCell>
                    {canEdit ? (
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label="Cancel"
                          className="text-destructive"
                          onClick={() => {
                            deleteIntakeJob.id
                              ? setAbortJob(deleteIntakeJob)
                              : setNewIntakesForDeletion((prevRows) =>
                                  prevRows.filter(
                                    (entity) =>
                                      entity.migrationEntityId !==
                                      deleteIntakeJob.migrationEntityId
                                  )
                                );
                          }}
                        >
                          {deleteIntakeJob.status ===
                          JobRequestStatus.Completed ? (
                            <Trash2 className="size-4" />
                          ) : (
                            <XCircle className="size-4" />
                          )}
                        </Button>
                      </TableCell>
                    ) : (
                      <TableCell />
                    )}
                  </TableRow>
                );
              })}
              {allRows.length === 0 && !isFetching && (
                <TableRow>
                  <TableCell colSpan={99} className="h-24 text-center text-muted-foreground">
                    No pending deletions.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
      </div>
      {abortJob ? (
        <AbortJobModal
          open={abortJob !== null}
          jobRequest={abortJob}
          onClose={() => {
            setAbortJob(null);
          }}
          onConfirm={async () => {
            await jobsRepository.deleteJob(abortJob.id);
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS] });
            queryClient.invalidateQueries({ queryKey: [
              QUERY_KEYS.INTAKES_DELETION_STATUS,
              organizationId,
            ] });
            if (abortJob.status !== JobRequestStatus.Completed) {
              queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INTAKES, organizationId] });
            }
            setAbortJob(null);
          }}
        />
      ) : (
        <></>
      )}

      {confirmDeletionModalOpen ? (
        <ConfirmMigrationEntitiesDeletionModal
          open={confirmDeletionModalOpen}
          entityKeys={newIntakesForDeletion.map((c) => c.intakeNumber)}
          confirmMigrationDeletionConfig={CONFIRM_MIGRATION_ENTITIES_DELETION_CONFIGS.INTAKES}
          onClose={() => {
            setConfirmDeletionModalOpen(false);
          }}
          onConfirm={async () => {
            await jobsRepository.startIntakesDeletion(
              organizationId,
              newIntakesForDeletion.map((c) => c.migrationEntityId)
            );
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS] });
            queryClient.invalidateQueries({ queryKey: [
              QUERY_KEYS.INTAKES_DELETION_STATUS,
              organizationId,
            ] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INTAKES, organizationId] });
            setNewIntakesForDeletion([]);
            setConfirmDeletionModalOpen(false);
            toast.success("Deletion started");
          }}
        />
      ) : (
        <></>
      )}
    </div>
  );
};

export default DeleteIntakesTable;
